import React, { useEffect, useRef, useState } from 'react';

import { washHub } from 'store';

import { useWashHubListener } from 'hooks';
import { useDebouncedValue } from 'hooks/useDebouncedValue';

import { QueryFunctionContext, useMutation, useQuery } from 'react-query';
import {
  fetchHarvestSummaryCsvData,
  fetchHarvestSummarys,
  getReportCounts,
} from 'api/reports';

import { HarvestSummaryProps, Tabs } from './HarvestSummary.props';

import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

import HarvestSummaryView from './HarvestSummary.view';
import { manualRefreshTrigger } from 'api/reports';
import { InventoryReportStatusEnum as statusEnum } from 'types/report';

const HarvestSummaryContainer = (props: HarvestSummaryProps) => {
  const queryUrl = useQueryParams();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<number | undefined>(undefined);
  const [sites, setSites] = useState<string[]>();
  const [accountManagers, setAccountManagers] = useState<string[]>();
  const [isSentToClient, setIsSentToClient] = useState<boolean | undefined>();
  const [feeAdjustmentTypes, setFeeAdjustmentTypes] = useState<string[]>();
  const [endDate, setEndDate] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [isArchiveTab, setIsArchiveTab] = useState(false);
  const [status, setStatus] = useState<number | undefined>();
  const [tabIdx, setTabIdx] = useState<number>(+(queryUrl.tabIdx || 0));
  // Seeded from the URL so a shared link searches on first load, without
  // waiting out the debounce.
  const [searchInput, setSearchInput] = useState(queryUrl.search ?? '');
  const search = useDebouncedValue(searchInput.trim());
  const [pageSearch, setPageSearch] = useState(search);
  const [urlRecord, setUrlRecord] = useState({});
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);
  const washHubId = washHub.selectors.useSelectedWashHubId();
  const isUpdated = useUrlSharing(urlRecord, washHubId);
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const csvRef = useRef<any>(null);

  // A new search can have fewer pages than the one being viewed. Resetting
  // during render (not in an effect) avoids fetching the stale page first.
  if (search !== pageSearch) {
    setPageSearch(search);
    setPage(1);
  }
  const { mutate: mutateCsv } = useMutation(
    async () => {
      const response = await fetchHarvestSummaryCsvData({
        page,
        limit: 10,
        startDate,
        endDate,
        site: sites,
        isArchived: isArchiveTab,
        washHubId,
        status,
        accountManagers,
        isSentToClient,
      });
      return response;
    },
    {
      onSuccess: (csvData) => {
        // csvData is already a string, proceed with creating the blob
        const blob = new Blob([String(csvData)], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'harvest-summary.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      },
    },
  );
  useEffect(() => {
    if (tabIdx) {
      setStatus(getHarvestSummaryStatusFromTabIndex(tabIdx));
    }
  }, [tabIdx]);

  useEffect(() => {
    const urlParams = {
      washHubId: String(washHubId),
      ...(status ? { status } : {}),
      ...(tabIdx ? { tabIdx } : {}),
      ...(isArchiveTab ? { isArchiveTab: `${isArchiveTab}` } : {}),
      ...(startDate ? { startDate: `${startDate.valueOf()}` } : {}),
      ...(endDate ? { endDate: `${endDate.valueOf()}` } : {}),
      ...(sites ? { sites: sites.join('-') } : {}),
      ...(accountManagers && accountManagers.length
        ? { accountManagers: accountManagers.join('-') }
        : {}),
      ...(typeof isSentToClient === 'boolean'
        ? { isSentToClient: `${isSentToClient}` }
        : {}),
      ...(feeAdjustmentTypes
        ? { feeAdjustmentTypes: feeAdjustmentTypes.join('-') }
        : {}),
      ...(search ? { search } : {}),
    };
    setUrlRecord(urlParams);
  }, [
    washHubId,
    endDate,
    startDate,
    status,
    sites,
    accountManagers,
    isSentToClient,
    isArchiveTab,
    feeAdjustmentTypes,
    search,
  ]);

  useEffect(() => {
    const {
      sites,
      isArchiveTab,
      startDate,
      endDate,
      tabIdx,
      accountManagers,
      isSentToClient,
      feeAdjustmentTypes,
    } = queryUrl;
    status && setStatus(+status);
    tabIdx && setTabIdx(+tabIdx);
    sites && setSites(sites.split('-'));
    accountManagers && setAccountManagers(accountManagers.split('-'));
    typeof isSentToClient === 'string' &&
      setIsSentToClient(isSentToClient === 'true');
    feeAdjustmentTypes && setFeeAdjustmentTypes(feeAdjustmentTypes.split('-'));
    isArchiveTab && setIsArchiveTab(isArchiveTab === 'true');
    startDate && setStartDate(new Date(+startDate));
    endDate && setEndDate(new Date(+endDate));
    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  const queryKey = [
    'fetch-reports',
    page,
    washHubId,
    sites?.join(''),
    accountManagers?.join(''),
    isSentToClient,
    feeAdjustmentTypes?.join(''),
    endDate,
    startDate,
    isArchiveTab,
    status,
    sortBy,
    sort,
    search,
  ];
  const queryFn = ({ signal }: QueryFunctionContext) =>
    fetchHarvestSummarys(
      {
        page,
        limit: 10,
        startDate,
        endDate,
        site: sites,
        isArchived: isArchiveTab,
        washHubId,
        status,
        ...(accountManagers && accountManagers.length
          ? { accountManagers }
          : {}),
        ...(typeof isSentToClient === 'boolean' ? { isSentToClient } : {}),
        ...(feeAdjustmentTypes && feeAdjustmentTypes.length
          ? { feeAdjustmentTypes }
          : {}),
        sortByField: sortBy,
        generateImpact: false,
        sort,
        ...(search ? { search } : {}),
      },
      signal,
    );

  const { data: reportCounts } = useQuery(
    [
      `report-total-counts`,
      washHubId,
      startDate,
      endDate,
      sites?.join(''),
      accountManagers?.join(''),
      isSentToClient,
    ],
    () =>
      getReportCounts({
        startDate,
        endDate,
        site: sites,
        washHubId,
        accountManagers,
        isSentToClient,
      }),
    {
      enabled: isWashHubIdValid,
      keepPreviousData: true,
    },
  );

  const options = {
    keepPreviousData: true,
    enabled: isWashHubIdValid, //!!washHubId
  };

  const { data, isLoading, isFetching } = useQuery(queryKey, queryFn, options);
  const { mutate, isSuccess, isLoading: loadingReport } = useMutation(
    manualRefreshTrigger,
    {
      onSuccess: () => {
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      },
    },
  );

  useWashHubListener(() => {
    setPage(1);
    setSites(undefined);
    setEndDate(undefined);
    setStartDate(undefined);
    setIsSentToClient(undefined);
  });

  const counts = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const sortHandler = (field: string) => {
    setSortBy(field);
    const sortOrder = sort === undefined || sort === 1 ? -1 : 1;
    setSort(sortOrder);
  };

  const getHarvestSummaryStatusFromTabIndex = (tabIndex: Tabs): any | null => {
    switch (tabIndex) {
      case Tabs.IN_REVIEW:
        return statusEnum.IN_REVIEW;
      case Tabs.READY_FOR_IMPACT:
        return statusEnum.READY_FOR_IMPACT;
      case Tabs.READY_FOR_INVOICE:
        return statusEnum.READY_FOR_INVOICE;
      case Tabs.DONE:
        return statusEnum.DONE;
      default:
        return undefined; // For Tabs.ALL and Tabs.ARCHIVE or any other unmapped tab
    }
  };

  const onTabChange = (index: Tabs) => {
    setTabIdx(index);
    setPage(1);
    if (index === Tabs.ARCHIVE) {
      setIsArchiveTab(true);
      setStatus(undefined);
      return;
    }
    const status = getHarvestSummaryStatusFromTabIndex(index);
    setStatus(status);
    setIsArchiveTab(false);
  };

  if (!isUrlMapped) return null;

  return (
    <HarvestSummaryView
      tabIdx={tabIdx}
      counts={counts}
      sites={sites}
      accountManagers={accountManagers}
      sentToClient={isSentToClient}
      feeAdjustmentTypes={feeAdjustmentTypes}
      status={status}
      endDate={endDate}
      startDate={startDate}
      pagination={pagination}
      setSites={setSites}
      setAccountManagers={setAccountManagers}
      setSentToClient={setIsSentToClient}
      setFeeAdjustmentTypes={setFeeAdjustmentTypes}
      setEndDate={setEndDate}
      setStartDate={setStartDate}
      isLoading={isLoading || isFetching || loadingReport}
      isSuccess={isSuccess}
      onRefresh={() => mutate()}
      onTabChange={onTabChange}
      csvRef={csvRef}
      mutateCsv={mutateCsv}
      isArchiveTab={isArchiveTab}
      sortHandler={sortHandler}
      reportCounts={reportCounts || {}}
      searchInput={searchInput}
      onSearchChange={setSearchInput}
    />
  );
};

export default HarvestSummaryContainer;
